import _ from 'lodash';
import { useLocale } from 'locale';
import React, { useCallback, useState } from 'react';
import * as Styled from './styled';
import { PoiId } from 'utils/entities';

import HebrewContactImage from 'components/unit-page/contact-agent/HebrewContactImageModeB';
import { Route } from 'config/routes';
import { IContactPageSubmitVariables } from 'store/sagas/apiService/types';
import { Link } from 'components/link';
import { AgentContactProps } from 'components/contact/types';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { Button } from 'ds/components/button';
import CheckIcon from 'assets/svg/ok-check.svg';
import MailIcon from 'assets/svg/send.svg';
import { ContactModalOpenMeta, ContactAgentModalSource } from 'store/state/app';
import useWhatsAppHref from 'hooks/useWhatsAppHref';
import ContactButtons from 'components/contact-buttons';
import ContactsWithWhatsAppExpandable from 'components/contact/whatsapp-expandable';
import { PhoneButton } from 'components/phone-button';
import { createPhoneURI } from 'hooks/useDisplayPhone';
import PhoneIcon from 'assets/svg/phone-new.svg';

interface POIGalleryAgentStickyProps extends AgentContactProps {
  id: PoiId;
  onSetContactModalPoiId: (id: PoiId, meta: ContactModalOpenMeta) => void;
  onCallClickEvent?: (isVisible: boolean, formValues: IContactPageSubmitVariables, source?: string) => void;
}

const getDataAutoStr = (sectionName: string) => `agent-poi-sticky-${sectionName}`;

export const POIGalleryAgentSticky: React.FC<POIGalleryAgentStickyProps> = (props) => {
  const {
    hideAgentInfo,
    showOnlyOffice,
    isDiscounted,
    agentId,
    officeId,
    agentName,
    officeName,
    isExclusive,
    whatsappNumber,
    onCallClickEvent,
    leadApi3Type,
    onSetContactModalPoiId,
    isContacted,
    contactValues,
    phone,
    user,
    poi,
    id,
  } = props;

  const { t } = useLocale();
  const [ showPhone, setShowPhone ] = useState(false);
  const screenBreakpoint = useScreenBreakpoint();
  const isMobile = screenBreakpoint < 3;
  const validPhoneNumber = Boolean(whatsappNumber);
  const whatsAppHref = useWhatsAppHref(whatsappNumber, agentName, id);

  const onShowPhone = useCallback((e: React.SyntheticEvent<HTMLElement>) => {
    onCallClickEvent(isMobile || showPhone, { ...contactValues, id, type: poi.type }, ContactAgentModalSource.AllGalleryList);
    if (!showPhone && !isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setShowPhone(true);
    }
  }, [ showPhone, setShowPhone, isMobile ]);

  const handleModalOpen = () => {
    onSetContactModalPoiId(id, { source: ContactAgentModalSource.AllGalleryList });
  };

  if (isMobile) {
    return (
      <Styled.Root data-auto="agent-poi-sticky">
        <Styled.ButtonWrapper justifyContent={isMobile} data-auto="agent-contact-poi-sticky">
          <ContactsWithWhatsAppExpandable
            poi={poi}
            user={user}
            leadApi3Type={leadApi3Type}
            userPhone={contactValues.phone}
            whatsAppHref={whatsAppHref}
            whatsappNumber={whatsappNumber}
            validPhoneNumber={validPhoneNumber}
          >
            <PhoneButton onClick={onShowPhone} href={createPhoneURI(phone)} data-auto="agent-contact-poi-button-sticky-text" size="extraLarge">
              <PhoneIcon width={24} height={24} />
              {showPhone ? phone : t('contactAgentModal.callButton')}
            </PhoneButton>
          </ContactsWithWhatsAppExpandable>

          {validPhoneNumber ? null : (
            <>
              {isContacted ? (
                <Button isInitialIconColor disabled data-auto="phone-private-inner-number-button-modal-disabled" size="extraLarge" mode="ghost">
                  <CheckIcon width={24} height={24} />
                  {t('contactAgentModal.button.disabled.modeB.static')}
                </Button>
              ) : (
                <Button isInitialIconColor onClick={handleModalOpen} data-auto="phone-private-inner-number-button-modal" size="extraLarge" mode="ghost">
                  <MailIcon width={24} height={24} />
                  {t('contactAgentModal.open')}
                </Button>
              )}
            </>
          )}
        </Styled.ButtonWrapper>
      </Styled.Root>
    );
  }

  return (
    <Styled.Root data-auto="agent-gallery-poi-sticky">
      <Styled.Details>
        <HebrewContactImage data-auto={getDataAutoStr('image')} showOnlyOffice={showOnlyOffice} nonClickableAgent={isDiscounted} poc={(poi as any).poc} />
        <Styled.Texts>
          <Link routeName={showOnlyOffice ? Route.OfficePage : Route.AgentPage} routeParams={{ id: showOnlyOffice ? officeId : agentId, source: 'listing_page' }}>
            <Styled.AgentName weight="bold"  data-auto="poc-name">
              {showOnlyOffice ? officeName : agentName}
            </Styled.AgentName>
          </Link>
          {hideAgentInfo ? null : (
            <div>
              <Styled.AgentText>{t('unitPage.contactAgentHebrewExclusive', { exclusive: isExclusive })}</Styled.AgentText>
            </div>
          )}
        </Styled.Texts>
      </Styled.Details>
      <ContactButtons
        phone={phone}
        onShowPhone={onShowPhone}
        isContacted={isContacted}
        handleModalOpen={handleModalOpen}
        showPhone={showPhone}
        dataAttributePrefix="agent-gallery-poi-sticky"
        source={ContactAgentModalSource.AllGalleryList}
      />
    </Styled.Root>
  );
};
